import * as React from 'react';
import { actionRequiredService, awsService } from '@/api';
import { DetectedPdfField, RequiredField, SignDocument } from '@/interfaces';
import { ClientSignFieldValue } from './useClientSignFields';
import { formatErrorMessage } from '@/lib';
import { PdfFormField } from '@/lib/matchPdfFormFields';
import { useEditDocument } from '@/hooks/useEditDocument';
import { useNotification } from '@/hooks';
import { isValidatedClientDataType } from '../lib/mockDataTypes';
import {
	applyRequiredFieldLabels,
	dataTypesFromRequiredFields,
	fieldsFromExtracted,
	previousResponsesFromRequiredFields,
	questionIdsFromRequiredFields,
} from '../lib/backendPdfFields';
import { useClientSettingsLookup } from './useClientSettingsLookup';
import { useClientSignFields } from './useClientSignFields';

const EMPTY_REQUIRED_FIELDS: RequiredField[] = [];

const splitFullName = (
	fullName: string
): { firstName: string; lastName: string } => {
	const parts = fullName.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return { firstName: '', lastName: '' };
	if (parts.length === 1) return { firstName: parts[0], lastName: '' };
	return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

export interface UseClientSignSessionArgs {
	applicationId: number | undefined;
	actionRequiredId: number | undefined;
	extractedFields?: PdfFormField[];
	requiredFields?: RequiredField[];
	onClose: () => void;
	onSubmitted?: () => void;
	refetch?: () => void;
	preview?: boolean;
}

export const useClientSignSession = ({
	applicationId,
	actionRequiredId,
	extractedFields,
	requiredFields = EMPTY_REQUIRED_FIELDS,
	onClose,
	onSubmitted,
	refetch,
	preview = false,
}: UseClientSignSessionArgs) => {
	const { toastError } = useNotification();
	const { getTrackingData } = useEditDocument();
	const {
		settings,
		isLoading: settingsLoading,
		isError: settingsError,
		displayName,
		firstName: settingsFirstName,
		lastName: settingsLastName,
	} = useClientSettingsLookup(!preview);

	const [detectedFields, setDetectedFields] = React.useState<
		DetectedPdfField[]
	>([]);
	const [hasDetected, setHasDetected] = React.useState(false);
	const [detectionError, setDetectionError] = React.useState(false);
	const [canvasKey, setCanvasKey] = React.useState(0);
	const [focusedFieldId, setFocusedFieldId] = React.useState<string | null>(
		null
	);
	const [signatureFieldId, setSignatureFieldId] = React.useState<string | null>(
		null
	);
	const [mismatchVisible, setMismatchVisible] = React.useState(false);
	const [mismatchIndex, setMismatchIndex] = React.useState(0);
	const [isReverifying, setIsReverifying] = React.useState(false);
	const [showSubmitAnyway, setShowSubmitAnyway] = React.useState(false);
	const [showDecline, setShowDecline] = React.useState(false);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [backendRejected, setBackendRejected] = React.useState(false);

	const presetFields = React.useMemo(
		() => fieldsFromExtracted(extractedFields, requiredFields),
		[extractedFields, requiredFields]
	);
	const dataTypeByFieldName = React.useMemo(
		() => dataTypesFromRequiredFields(requiredFields),
		[requiredFields]
	);
	const previousResponseByFieldName = React.useMemo(
		() => previousResponsesFromRequiredFields(requiredFields),
		[requiredFields]
	);
	const questionIdByFieldName = React.useMemo(
		() => questionIdsFromRequiredFields(requiredFields),
		[requiredFields]
	);

	const {
		fields,
		setFieldValue: commitFieldValue,
		allComplete,
		hasDetectedFields,
		mismatchedFieldIds,
	} = useClientSignFields(
		detectedFields,
		settings,
		dataTypeByFieldName,
		previousResponseByFieldName,
		questionIdByFieldName
	);

	const visibleMismatches =
		mismatchVisible && !backendRejected ? mismatchedFieldIds : [];

	React.useEffect(() => {
		if (mismatchIndex >= visibleMismatches.length) {
			setMismatchIndex(Math.max(0, visibleMismatches.length - 1));
		}
	}, [mismatchIndex, visibleMismatches.length]);

	const settingsByFieldId = React.useMemo(() => {
		const map: Record<string, string> = {};
		if (settingsError) return map;
		Object.entries(fields).forEach(([id, field]) => {
			const stored = settings[field.dataType];
			if (stored) map[id] = stored;
		});
		return map;
	}, [fields, settings, settingsError]);

	const handleFieldsDetected = React.useCallback(
		(nextFields: DetectedPdfField[]) => {
			setDetectedFields(applyRequiredFieldLabels(nextFields, requiredFields));
			setHasDetected(true);
			setDetectionError(false);
		},
		[requiredFields]
	);

	React.useEffect(() => {
		if (!presetFields?.length || hasDetected) return;
		handleFieldsDetected(presetFields);
	}, [presetFields, hasDetected, handleFieldsDetected]);

	const handleDetectionError = React.useCallback(() => {
		setDetectionError(true);
		setHasDetected(true);
	}, []);

	const retryDetection = React.useCallback(() => {
		setDetectionError(false);
		setHasDetected(false);
		setDetectedFields([]);
		setCanvasKey((key) => key + 1);
	}, []);

	const handleFieldValueChange = React.useCallback(
		(fieldId: string, value: ClientSignFieldValue) => {
			commitFieldValue(fieldId, value);
			setBackendRejected(false);
			setMismatchVisible(false);
			setShowSubmitAnyway(false);
		},
		[commitFieldValue]
	);

	const handleApplySuggestion = React.useCallback(
		(fieldId: string) => {
			const stored = settingsByFieldId[fieldId];
			if (!stored) return;
			handleFieldValueChange(fieldId, stored);
		},
		[handleFieldValueChange, settingsByFieldId]
	);

	const handleSignatureApply = React.useCallback(
		(dataUrl: string) => {
			if (!signatureFieldId) return;
			handleFieldValueChange(signatureFieldId, dataUrl);
			setSignatureFieldId(null);
		},
		[handleFieldValueChange, signatureFieldId]
	);

	const uploadSignature = React.useCallback(async (dataUrl: string) => {
		const signatureBlob = await fetch(dataUrl).then((response) =>
			response.blob()
		);
		const signatureFile = new File([signatureBlob], 'signature.png', {
			type: 'image/png',
		});
		const uploaded = await awsService.uploadImage(signatureFile);
		return uploaded.data.data.location;
	}, []);

	const buildResponses = React.useCallback(async () => {
		const signatureEntries = Object.entries(fields).filter(
			([, field]) =>
				field.fieldType === 'signature' &&
				typeof field.value === 'string' &&
				field.value.trim().length > 0
		);
		const uploadedSignatures = await Promise.all(
			signatureEntries.map(async ([id, field]) => {
				const value = field.value as string;
				const isNew = value.startsWith('data:');
				const url = isNew ? await uploadSignature(value) : value;
				return { id, url, isNew };
			})
		);
		const signatureUrlById = Object.fromEntries(
			uploadedSignatures.map(({ id, url }) => [id, url])
		);
		const signatureUrl =
			uploadedSignatures.find(({ isNew }) => isNew)?.url ??
			uploadedSignatures[0]?.url ??
			'';

		const responses = Object.entries(fields)
			.filter(([, field]) => field.questionId && !field.readOnly)
			.map(([id, field]) => ({
				id: field.questionId as string,
				response:
					field.fieldType === 'signature' && signatureUrlById[id]
						? signatureUrlById[id]
						: String(field.value ?? ''),
			}));

		return { responses, signatureUrl };
	}, [fields, uploadSignature]);

	const resolveNames = React.useCallback(() => {
		if (settingsFirstName || settingsLastName) {
			return { firstName: settingsFirstName, lastName: settingsLastName };
		}
		const fullNameField = Object.values(fields).find(
			(field) => field.dataType === 'FULL_NAME'
		);
		const overlayName =
			typeof fullNameField?.value === 'string' ? fullNameField.value : '';
		if (overlayName.trim()) return splitFullName(overlayName);
		return splitFullName(displayName);
	}, [displayName, fields, settingsFirstName, settingsLastName]);

	const submitDocument = React.useCallback(
		async (approved: boolean) => {
			if (preview) return;
			if (!applicationId || !actionRequiredId || isSubmitting) return;
			try {
				setIsSubmitting(true);
				const { firstName, lastName } = resolveNames();
				const tracking = await getTrackingData();
				const { responses, signatureUrl } = approved
					? await buildResponses()
					: { responses: [], signatureUrl: '' };
				const payload: SignDocument = {
					application_id: String(applicationId),
					action_id: String(actionRequiredId),
					signature: approved ? signatureUrl : '',
					firstName,
					lastName,
					status: String(approved),
					doc: undefined as unknown as Blob,
					ip: tracking.ip,
					device: tracking.device,
					browser: tracking.browser,
					country: tracking.country,
					responses: JSON.stringify(responses),
				};
				await actionRequiredService.signDocument(payload);
				setBackendRejected(false);
				if (approved) {
					onSubmitted?.();
				} else {
					refetch?.();
					onClose();
				}
			} catch (error) {
				const rawMessage =
					typeof error === 'object' && error !== null && 'message' in error
						? (error as { message?: string | string[] }).message
						: undefined;
				toastError(formatErrorMessage(rawMessage));
			} finally {
				setIsSubmitting(false);
				setShowSubmitAnyway(false);
				setShowDecline(false);
			}
		},
		[
			actionRequiredId,
			applicationId,
			buildResponses,
			displayName,
			getTrackingData,
			isSubmitting,
			onClose,
			onSubmitted,
			preview,
			refetch,
			resolveNames,
			toastError,
		]
	);

	const handleConfirm = React.useCallback(
		(scrollToField?: (fieldId: string) => void) => {
			if (preview || isSubmitting) return;
			setBackendRejected(false);
			if (hasDetectedFields && !allComplete) return;
			if (settingsError) {
				void submitDocument(true);
				return;
			}
			if (mismatchedFieldIds.length > 0) {
				setMismatchVisible(true);
				setMismatchIndex(0);
				const firstId = mismatchedFieldIds[0];
				if (firstId) {
					window.setTimeout(() => scrollToField?.(firstId), 400);
				}
				return;
			}
			void submitDocument(true);
		},
		[
			allComplete,
			hasDetectedFields,
			isSubmitting,
			mismatchedFieldIds,
			preview,
			settingsError,
			submitDocument,
		]
	);

	const handleReverify = React.useCallback(
		(scrollToField?: (fieldId: string) => void) => {
			if (isReverifying) return;
			setIsReverifying(true);
			window.setTimeout(() => {
				if (mismatchedFieldIds.length === 0) {
					setMismatchVisible(false);
				} else {
					setMismatchIndex(0);
					const firstId = mismatchedFieldIds[0];
					if (firstId) {
						window.setTimeout(() => scrollToField?.(firstId), 400);
					}
				}
				setIsReverifying(false);
			}, 400);
		},
		[isReverifying, mismatchedFieldIds]
	);

	const zeroFieldsNotice = hasDetected && !detectionError && !hasDetectedFields;
	const confirmEnabled =
		!isSubmitting &&
		!settingsLoading &&
		hasDetected &&
		!detectionError &&
		(!hasDetectedFields || allComplete);

	const suggestionFocusId =
		settingsError || mismatchVisible ? null : focusedFieldId;
	const suggestionAllowed = Boolean(
		suggestionFocusId &&
			fields[suggestionFocusId] &&
			isValidatedClientDataType(fields[suggestionFocusId].dataType)
	);

	return {
		detectedFields,
		fields,
		setFieldValue: handleFieldValueChange,
		hasDetectedFields,
		mismatchedFieldIds,
		settingsError,
		settingsLoading,
		presetFields,
		detectionError,
		hasDetected,
		canvasKey,
		focusedFieldId,
		setFocusedFieldId,
		signatureFieldId,
		setSignatureFieldId,
		mismatchVisible,
		mismatchIndex,
		setMismatchIndex,
		isReverifying,
		showSubmitAnyway,
		setShowSubmitAnyway,
		showDecline,
		setShowDecline,
		isSubmitting,
		backendRejected,
		visibleMismatches,
		settingsByFieldId,
		handleFieldsDetected,
		handleDetectionError,
		retryDetection,
		handleApplySuggestion,
		handleSignatureApply,
		submitDocument,
		handleConfirm,
		handleReverify,
		zeroFieldsNotice,
		confirmEnabled,
		suggestionFocusId,
		suggestionAllowed,
	};
};

export type ClientSignSession = ReturnType<typeof useClientSignSession>;
